<template>
  <div class="file-preview" data-testid="file-preview">
    <div
      v-if="canShowInline && previewKind === 'pdf'"
      class="file-preview__pdf-wrap"
      data-testid="file-preview-pdf"
    >
      <p v-if="pdfPreviewState === 'loading'" class="file-preview__status">Loading preview...</p>
      <p v-else-if="pdfPreviewState === 'error'" class="file-preview__status">
        Couldn't render this document inline.
      </p>
      <div ref="pdfContainerRef" class="file-preview__pdf" />
    </div>
    <img
      v-else-if="canShowInline && previewKind === 'image'"
      class="file-preview__image"
      data-testid="file-preview-image"
      :src="previewUrl"
      :alt="file.name"
    />
    <iframe
      v-else-if="canShowInline && previewKind === 'doc' && officeViewerSrc"
      class="file-preview__frame"
      data-testid="file-preview-frame"
      :src="officeViewerSrc"
      :title="file.name"
    />
    <iframe
      v-else-if="canShowInline && previewKind === 'docx' && useOfficeViewerFallback && officeViewerSrc"
      class="file-preview__frame"
      data-testid="file-preview-frame"
      :src="officeViewerSrc"
      :title="file.name"
    />
    <div
      v-else-if="canShowInline && previewKind === 'docx'"
      class="file-preview__docx-wrap"
      data-testid="file-preview-docx"
    >
      <p v-if="docxPreviewState === 'loading'" class="file-preview__status">Loading preview...</p>
      <p v-else-if="docxPreviewState === 'error'" class="file-preview__status">
        Couldn't render this document inline.
      </p>
      <div ref="docxStyleContainerRef" class="file-preview__docx-styles" aria-hidden="true" />
      <div ref="docxContainerRef" class="file-preview__docx" />
    </div>

    <div v-else class="file-preview__fallback" data-testid="file-preview-fallback">
      <p class="file-preview__fallback-title">{{ fallbackTitle }}</p>
      <p class="file-preview__fallback-message">{{ fallbackMessage }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { renderAsync } from "docx-preview";
import { getPdfDocument, renderPdfPagesToContainer } from "./renderPdfToContainer";

const OFFICE_VIEWER_BASE = "https://view.officeapps.live.com/op/embed.aspx?src=";
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];

const props = defineProps({
  file: {
    type: Object,
    default: () => null,
  },
});

const docxContainerRef = ref(null);
const docxStyleContainerRef = ref(null);
const pdfContainerRef = ref(null);
const docxPreviewState = ref("idle");
const pdfPreviewState = ref("idle");
const useOfficeViewerFallback = ref(false);
const objectUrl = ref("");
let docxRenderRequestId = 0;
let pdfRenderRequestId = 0;
let loadedPdf = null;
let loadedPdfSource = "";
let lastPdfWidth = 0;
let pdfResizeObserver = null;

function getFileExtension(name) {
  const match = /\.([a-zA-Z0-9]+)$/.exec(name || "");
  return match ? match[1].toLowerCase() : "";
}

function isDocxType(rawTypeValue, extension) {
  return (
    rawTypeValue.includes("wordprocessingml") ||
    rawTypeValue === "docx" ||
    extension === "docx"
  );
}

function isDocType(rawTypeValue, extension) {
  return (
    rawTypeValue === "application/msword" ||
    rawTypeValue === "msword" ||
    extension === "doc"
  );
}

function revokeObjectUrl() {
  if (objectUrl.value && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
    URL.revokeObjectURL(objectUrl.value);
  }
  objectUrl.value = "";
}

function syncObjectUrl() {
  revokeObjectUrl();
  const blob = props.file?.file;
  if (
    blob &&
    typeof URL !== "undefined" &&
    typeof URL.createObjectURL === "function"
  ) {
    objectUrl.value = URL.createObjectURL(blob);
  }
}

const rawType = computed(() =>
  String(props.file?.type || props.file?.contentType || "").toLowerCase()
);
const typeToken = computed(() => {
  const raw = rawType.value;
  if (!raw) return "";
  return raw.includes("/") ? raw.split("/").pop() : raw;
});
const fileExtension = computed(() => getFileExtension(props.file?.name));

const previewKind = computed(() => {
  if (!props.file) return null;
  if (
    rawType.value === "application/pdf" ||
    typeToken.value === "pdf" ||
    fileExtension.value === "pdf"
  ) {
    return "pdf";
  }
  if (
    rawType.value.startsWith("image/") ||
    IMAGE_EXTENSIONS.includes(fileExtension.value) ||
    typeToken.value === "svg"
  ) {
    return "image";
  }
  if (isDocxType(rawType.value, fileExtension.value)) {
    return "docx";
  }
  if (isDocType(rawType.value, fileExtension.value)) {
    return "doc";
  }
  return null;
});

const previewUrl = computed(() => objectUrl.value || props.file?.url || "");
const canUseOfficeViewer = computed(
  () =>
    !!previewUrl.value &&
    !previewUrl.value.startsWith("blob:") &&
    !previewUrl.value.startsWith("file:")
);
const officeViewerSrc = computed(() => {
  if (!canUseOfficeViewer.value) return null;
  return `${OFFICE_VIEWER_BASE}${encodeURIComponent(previewUrl.value)}`;
});
const canShowInline = computed(() => !!previewUrl.value && previewKind.value !== null);

const fallbackTitle = computed(() => {
  if (!previewUrl.value) return "Preview unavailable";
  return "This file type can't be previewed here";
});

const fallbackMessage = computed(() => {
  if (!previewUrl.value) {
    return `We can't show a preview for ${props.file?.name || "this file"} right now.`;
  }
  return `${props.file?.name || "This file"} can't be rendered inline.`;
});

function disconnectPdfObserver() {
  if (pdfResizeObserver) {
    pdfResizeObserver.disconnect();
    pdfResizeObserver = null;
  }
}

async function destroyLoadedPdf() {
  if (loadedPdf && typeof loadedPdf.destroy === "function") {
    try {
      await loadedPdf.destroy();
    } catch {
      // ignore worker teardown races
    }
  }
  loadedPdf = null;
  loadedPdfSource = "";
  lastPdfWidth = 0;
}

async function paintPdfPages(container, requestId) {
  if (!loadedPdf || !container || requestId !== pdfRenderRequestId) {
    return;
  }
  const width = Math.floor(container.clientWidth);
  if (!width || width === lastPdfWidth) {
    return;
  }
  lastPdfWidth = width;
  await renderPdfPagesToContainer(loadedPdf, container, width);
}

function observePdfContainer(container, requestId) {
  disconnectPdfObserver();
  if (!container || typeof ResizeObserver === "undefined") {
    return;
  }
  pdfResizeObserver = new ResizeObserver(() => {
    paintPdfPages(container, requestId).catch(() => {});
  });
  pdfResizeObserver.observe(container);
}

async function renderPdfPreview() {
  const requestId = ++pdfRenderRequestId;
  const url = previewUrl.value;

  if (previewKind.value !== "pdf" || !url) {
    disconnectPdfObserver();
    await destroyLoadedPdf();
    pdfPreviewState.value = "idle";
    return;
  }

  pdfPreviewState.value = "loading";
  await nextTick();

  const container = pdfContainerRef.value;
  if (!container || requestId !== pdfRenderRequestId) {
    return;
  }

  try {
    if (!loadedPdf || loadedPdfSource !== url) {
      await destroyLoadedPdf();
      loadedPdf = await getPdfDocument(url);
      loadedPdfSource = url;
      lastPdfWidth = 0;
    }
    if (requestId !== pdfRenderRequestId) {
      return;
    }
    await paintPdfPages(container, requestId);
    if (requestId !== pdfRenderRequestId) {
      return;
    }
    pdfPreviewState.value = "ready";
    observePdfContainer(container, requestId);
  } catch {
    if (requestId !== pdfRenderRequestId) {
      return;
    }
    container.innerHTML = "";
    pdfPreviewState.value = "error";
  }
}

async function renderDocxPreview() {
  const requestId = ++docxRenderRequestId;
  const url = previewUrl.value;

  docxPreviewState.value = "idle";
  useOfficeViewerFallback.value = false;

  if (previewKind.value !== "docx" || !url) {
    return;
  }

  docxPreviewState.value = "loading";
  await nextTick();

  const container = docxContainerRef.value;
  if (!container || requestId !== docxRenderRequestId) {
    return;
  }

  container.innerHTML = "";
  if (docxStyleContainerRef.value) {
    docxStyleContainerRef.value.innerHTML = "";
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch document (${response.status})`);
    }

    const blob = await response.blob();
    if (requestId !== docxRenderRequestId) {
      return;
    }

    await renderAsync(blob, container, docxStyleContainerRef.value, {
      className: "docx",
      inWrapper: true,
    });

    if (requestId !== docxRenderRequestId) {
      return;
    }

    docxPreviewState.value = "ready";
  } catch {
    if (requestId !== docxRenderRequestId) {
      return;
    }

    container.innerHTML = "";
    if (docxStyleContainerRef.value) {
      docxStyleContainerRef.value.innerHTML = "";
    }
    useOfficeViewerFallback.value = canUseOfficeViewer.value;
    docxPreviewState.value = "error";
  }
}

watch(
  () => [props.file?.file, props.file?.url, props.file?.name, props.file?.type],
  () => {
    syncObjectUrl();
    renderPdfPreview();
    renderDocxPreview();
  },
  { immediate: true }
);

onUnmounted(() => {
  disconnectPdfObserver();
  destroyLoadedPdf();
  revokeObjectUrl();
});
</script>

<style scoped>
.file-preview {
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #ffffff;
}

.file-preview__frame,
.file-preview__image,
.file-preview__pdf-wrap {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: #ffffff;
}

.file-preview__image {
  object-fit: contain;
  align-self: center;
}

.file-preview__pdf-wrap {
  overflow: auto;
}

.file-preview__pdf {
  width: 100%;
  min-height: 100%;
  background: #ffffff;
}

.file-preview__pdf :deep(canvas) {
  display: block;
  width: 100%;
  height: auto;
}

.file-preview__docx-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  overflow: auto;
  background: #ffffff;
}

.file-preview__docx-styles {
  display: none;
}

.file-preview__docx {
  width: 100%;
  min-height: 100%;
  padding: 16px;
  box-sizing: border-box;
}

.file-preview__docx :deep(.docx-wrapper) {
  margin: 0 auto;
  background: #ffffff;
}

.file-preview__status {
  margin: 0;
  padding: 24px;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 13px;
  color: #667085;
}

.file-preview__fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 240px;
  padding: 40px 24px;
  text-align: center;
}

.file-preview__fallback-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #101828;
}

.file-preview__fallback-message {
  margin: 0;
  max-width: 420px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  color: #667085;
}
</style>
